import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, type StyleProp, View, type ViewStyle } from "react-native";
import {
	Button,
	Dialog,
	Provider as PaperProvider,
	Portal,
} from "react-native-paper";
import { DatePickerModalContent } from "react-native-paper-dates";
import { getDatePickerLocale } from "../../helpers/date-picker-locale";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";

interface CustomDatePickerModalProps {
	isClearVisible?: boolean;
	mode?: "single" | "range";
	onClose?: () => void;
	onOk?: (value: Date | { startTime: Date; endTime: Date } | null) => void;
	onClear?: () => void;
	startDate?: Date;
	endDate?: Date;
	date?: Date;
	validRange?: {
		startDate?: Date;
		endDate?: Date;
	};
	style?: StyleProp<ViewStyle>;
	label?: string;
}

const CustomDatePickerModal = ({
	isClearVisible = true,
	mode = "single",
	onClose,
	onOk,
	onClear,
	startDate,
	endDate,
	date,
	validRange = {
		startDate: new Date(2020, 1, 1),
	},
	label,
}: CustomDatePickerModalProps) => {
	const { t, i18n } = useTranslation();
	const [range, setRange] = useState({
		startDate: startDate,
		endDate: endDate,
	});
	const [selectedDate, setSelectedDate] = useState<Date | undefined>(date);

	useEffect(() => {
		setRange({ startDate, endDate });
	}, [startDate, endDate]);

	useEffect(() => {
		setSelectedDate(date);
	}, [date]);

	const handleConfirm = (params: {
		startDate?: Date;
		endDate?: Date;
		date?: Date;
	}) => {
		if (mode === "range" && params.startDate && params.endDate) {
			setRange({ startDate: params.startDate, endDate: params.endDate });
			onOk?.({
				startTime: params.startDate,
				endTime: params.endDate,
			});
		} else if (mode === "single" && params.date) {
			setSelectedDate(params.date);
			onOk?.(params.date);
		}
		onClose?.();
	};

	const handleDismiss = () => {
		onClose?.();
	};

	const handleClear = () => {
		if (mode === "range") {
			setRange({ startDate: undefined, endDate: undefined });
		} else {
			setSelectedDate(undefined);
		}
		onClear?.();
	};

	const handleChange = (params: {
		startDate?: Date;
		endDate?: Date;
		date?: Date;
	}) => {
		if (mode === "range") {
			setRange({ startDate: params.startDate, endDate: params.endDate });
		} else {
			setSelectedDate(params.date);
		}
	};

	const theme = useAppTheme();
	return (
		<Portal>
			<Modal
				visible={true}
				transparent
				animationType="fade"
				onRequestClose={handleDismiss}
			>
				<View
					style={{
						flex: 1,
						justifyContent: "center",
						alignItems: "center",
						backgroundColor: "rgba(0, 0, 0, 0.5)",
					}}
				>
					<Dialog
						style={{
							width: 360,
							height: mode === "range" ? 696 : 554,
							backgroundColor: theme.colors.surfaceVariant,
							borderRadius: 28,
							overflow: "hidden",
							margin: 0,
						}}
						visible={true}
						onDismiss={handleDismiss}
					>
						<Box flex={1} display="flex" flexDirection="column">
							<Box flex={1}>
								<PaperProvider
									theme={{
										...theme,
										colors: {
											...theme.colors,
											surface: theme.colors.surfaceVariant,
										},
									}}
								>
									<DatePickerModalContent
										mode={mode}
										onChange={handleChange}
										locale={getDatePickerLocale(i18n.language)}
										startDate={range.startDate}
										endDate={range.endDate}
										date={selectedDate}
										onDismiss={handleDismiss}
										onConfirm={handleConfirm}
										validRange={validRange}
										label={label}
									/>
								</PaperProvider>
							</Box>
							<Box
								style={{
									backgroundColor: theme.colors.surfaceVariant,
									padding: 8,
									flexDirection: "row",
									justifyContent: "space-between",
								}}
							>
								<Box>
									{isClearVisible && (
										<Button
											onPress={handleClear}
											textColor={theme.colors.primary}
										>
											{t("common.clear")}
										</Button>
									)}
								</Box>
								<Box flexDirection="row" gap={8}>
									<Button
										onPress={handleDismiss}
										textColor={theme.colors.primary}
									>
										{t("common.cancel")}
									</Button>
									<Button
										disabled={!selectedDate}
										onPress={() => {
											if (
												mode === "range" &&
												range.startDate &&
												range.endDate
											) {
												onOk?.({
													startTime: range.startDate,
													endTime: range.endDate,
												});
											} else if (mode === "single" && selectedDate) {
												onOk?.(selectedDate);
											} else {
												onOk?.(null);
											}
										}}
										textColor={theme.colors.primary}
									>
										{t("common.ok")}
									</Button>
								</Box>
							</Box>
						</Box>
					</Dialog>
				</View>
			</Modal>
		</Portal>
	);
};
export default CustomDatePickerModal;
